<template>
    <nav
        v-if="shouldRender"
        class="flex flex-col gap-3 sm:flex-row sm:items-center"
        :class="setPerPage ? 'border-primary-400/20 w-full justify-between border-t px-3 py-2' : 'w-auto justify-end'"
        aria-label="Pagination"
    >
        <div v-if="setPerPage" class="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
            <div class="text-primary-300 flex min-w-0 items-center gap-1.5 text-xs tabular-nums">
                <template v-if="hasResults">
                    <span class="whitespace-nowrap">{{ rangeStart }}-{{ rangeEnd }}</span>
                    <span class="text-primary-400">/</span>
                </template>
                <span class="whitespace-nowrap">
                    <t :path="['generic.results', paginationData.total]" />
                </span>
            </div>

            <span v-if="showPerPage" class="bg-primary-500/30 hidden h-4 w-px sm:block" aria-hidden="true" />

            <label v-if="showPerPage" class="text-primary-300 flex items-center gap-2 text-xs">
                <span class="whitespace-nowrap"><t path="generic.per_page" /></span>
                <v-select
                    name="per_page"
                    class="w-20"
                    hide-label
                    no-translate
                    rule="required"
                    :options="normalizedPerPageOptions"
                    :value="paginationData.perPage"
                    @update:value="onPerPageChange"
                />
            </label>
        </div>

        <ul v-if="showPageControls" class="flex max-w-full flex-wrap items-center justify-start gap-1 sm:justify-end">
            <li>
                <v-button
                    type="button"
                    :disabled="firstPage"
                    :class="navigationButtonClass(firstPage)"
                    aria-label="Previous page"
                    @click.prevent="goToPage(currentPage - 1)"
                >
                    <fa :icon="['fas', 'chevron-left']" class="h-3 w-3" />
                </v-button>
            </li>
            <li v-for="(page, index) of pageElements" :key="`${page}-${index}`">
                <v-button
                    type="button"
                    :disabled="page === '...' || currentPage === page"
                    :aria-current="currentPage === page ? 'page' : undefined"
                    :class="pageButtonClass(page)"
                    :aria-label="typeof page === 'number' ? `Page ${page}` : undefined"
                    @click.prevent="goToPage(page)"
                >
                    {{ page }}
                </v-button>
            </li>
            <li>
                <v-button
                    type="button"
                    :disabled="lastPage"
                    :class="navigationButtonClass(lastPage)"
                    aria-label="Next page"
                    @click.prevent="goToPage(currentPage + 1)"
                >
                    <fa :icon="['fas', 'chevron-right']" class="h-3 w-3" />
                </v-button>
            </li>
        </ul>
    </nav>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';

type PageElement = number | '...';

/** Number of buttons the page list settles at, so its width stops jumping around as you page through. */
const PAGE_SLOTS = 7;

const pageRange = (from: number, to: number): number[] => Array.from({ length: to - from + 1 }, (_, i) => from + i);

export default defineComponent({
    props: {
        paginationData: {
            type: Object as PropType<PaginationResponse>,
            required: true,
        },
        setPage: {
            type: Function,
            required: true,
        },
        setPerPage: {
            type: Function,
        },
        perPageOptions: {
            type: Array as PropType<number[]>,
            default: () => [10, 25, 50],
        },
    },

    setup(props) {
        const hasResults = computed(() => props.paginationData.total > 0);
        const showPageControls = computed(() => props.paginationData.totalPages > 1);
        const showPerPage = computed(() => Boolean(props.setPerPage) && hasResults.value);

        const currentPage = computed(() => props.paginationData.currentPage);
        const rangeStart = computed(() => (hasResults.value ? (currentPage.value - 1) * props.paginationData.perPage + 1 : 0));
        const rangeEnd = computed(() =>
            hasResults.value ? Math.min(props.paginationData.total, rangeStart.value + props.paginationData.count - 1) : 0,
        );

        const buttonClass =
            'flex h-8 min-w-8 items-center justify-center rounded px-2 text-xs font-medium tabular-nums transition-colors focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:outline-none';
        const enabledButtonClass = `${buttonClass} bg-primary-500/30 text-primary-200 hover:bg-primary-500/60 hover:text-primary-50`;
        const disabledButtonClass = `${buttonClass} cursor-not-allowed bg-primary-500/30 text-primary-400`;
        const activeButtonClass = `${buttonClass} bg-primary-500 text-primary-50`;
        const ellipsisButtonClass = `${buttonClass} cursor-default bg-transparent text-primary-300`;

        const goToPage = (page: PageElement) => {
            if (page === '...' || page < 1 || page > props.paginationData.totalPages || page === currentPage.value) return;

            props.setPage(page);
        };

        return {
            currentPage,
            hasResults,
            rangeEnd,
            rangeStart,
            shouldRender: computed(() => showPerPage.value || showPageControls.value),
            showPageControls,
            showPerPage,
            firstPage: computed(() => props.paginationData.currentPage === 1),
            lastPage: computed(() => props.paginationData.currentPage === props.paginationData.totalPages),
            normalizedPerPageOptions: computed(() => [...new Set([...props.perPageOptions, props.paginationData.perPage])].sort((a, b) => a - b)),
            pageElements: computed(() => {
                const currentPage = props.paginationData.currentPage;
                const totalPages = props.paginationData.totalPages;

                if (totalPages <= PAGE_SLOTS) return pageRange(1, totalPages);

                const runLength = PAGE_SLOTS - 2;

                if (currentPage < runLength) return [...pageRange(1, runLength), '...' as const, totalPages];
                if (currentPage > totalPages - runLength + 1) return [1, '...' as const, ...pageRange(totalPages - runLength + 1, totalPages)];

                return [1, '...' as const, ...pageRange(currentPage - 1, currentPage + 1), '...' as const, totalPages];
            }),
            goToPage,
            navigationButtonClass: (disabled: boolean) => (disabled ? disabledButtonClass : enabledButtonClass),
            onPerPageChange: (value: unknown) => {
                const perPage = Number(value);
                if (perPage === props.paginationData.perPage) return;

                props.setPerPage?.(perPage);
            },
            pageButtonClass: (page: PageElement) => {
                if (page === '...') return ellipsisButtonClass;
                if (page === currentPage.value) return activeButtonClass;

                return enabledButtonClass;
            },
        };
    },
});
</script>
